import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchApi } from "@/lib/api";
import type { VoiceCard } from "@/lib/props-provenance";
import type { CompanyValue } from "@/types/recognition";
import type { TodoDTO } from "@/hooks/use-agent-todos";
import { useAcceptTodo, useApproveRun, useEditRun, usePrepareTodo } from "@/hooks/use-todo-contract";

/**
 * The drawer's Props view: its context read, shaping, the draft-first
 * setting, and Give Props — which runs the to-do contract in order (accept a
 * proposal, prepare, save the giver's exact message and value onto the run,
 * approve). The approval is held ~10 s behind Undo before the post goes out.
 */

export type PropsValue = CompanyValue;

/** ``GET /agent/todos/{key}/props``. */
export interface TodoPropsContext {
  facts: string[];
  source_label: string | null;
  coach_question: string;
  voice: VoiceCard | null;
  values: PropsValue[];
  l10_reach: string[];
  draft_first: boolean;
}

const path = (key: string, tail: string) => `/agent/todos/${encodeURIComponent(key)}/${tail}`;
const json = (body: unknown) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const todoPropsKey = (key: string) => ["agent-todo-props", key] as const;

export function useTodoPropsContext(key: string) {
  return useQuery<TodoPropsContext>({
    queryKey: todoPropsKey(key),
    queryFn: () => fetchApi(path(key, "props")),
    staleTime: 5 * 60_000,
  });
}

export function useShapeProps(key: string) {
  return useMutation<{ message: string; shaped: boolean }, Error, { line: string; mode: "coach" | "draft" }>({
    mutationFn: (body) => fetchApi(path(key, "props/shape"), json(body)),
  });
}

export function useSetPropsDraftFirst() {
  const queryClient = useQueryClient();
  return useMutation<{ draft_first: boolean }, Error, boolean>({
    mutationFn: (on) => fetchApi("/agent/props/draft-first", { ...json({ draft_first: on }), method: "PUT" }),
    // Per user, so every cached props context takes it (not only this to-do's).
    onSuccess: ({ draft_first }) =>
      queryClient.setQueriesData<TodoPropsContext>({ queryKey: ["agent-todo-props"] }, (prev) =>
        prev ? { ...prev, draft_first } : prev,
      ),
  });
}

/** The ``{value, message}`` the run is bound to now, or null with no run. */
export function boundProps(todo: TodoDTO): { value: string; message: string } | null {
  const args = (todo.run?.operation as { args?: { value?: unknown; message?: unknown } } | null)?.args;
  if (!args) return null;
  return { value: String(args.value ?? ""), message: String(args.message ?? "") };
}

/** Give Props: the existing contract mutations in order, each writing its row into the cache. */
export function useGiveProps() {
  const accept = useAcceptTodo();
  const prepare = usePrepareTodo();
  const edit = useEditRun();
  const approve = useApproveRun();
  return useMutation<TodoDTO, Error, { todo: TodoDTO; value: string; message: string }>({
    mutationFn: async ({ todo, value, message }) => {
      const key = todo.id;
      let row = todo;
      // Accept & prepare in one step (the accept stays shown if preparing fails).
      if (row.next_action === "accept") row = await accept.mutateAsync({ key, prepare: true });
      else if (!row.run || !["preparing", "awaiting_approval"].includes(row.run.state)) row = await prepare.mutateAsync(key);
      const bound = boundProps(row);
      if (row.run && (bound?.message !== message || bound?.value !== value)) {
        row = await edit.mutateAsync({ key, runId: row.run.id, args: { value, message } });
      }
      if (!row.run || row.run.state !== "awaiting_approval") {
        throw new Error(row.run?.error || "These props aren't ready to give yet");
      }
      return approve.mutateAsync({ key, runId: row.run.id });
    },
  });
}
